import AttendantStayTable from "./AttendantStayTable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FaCalendarCheck, FaPlus, FaSave, FaTrash, FaUserShield, FaUsers } from "react-icons/fa";
import API from "../../api/api";

const todayIST = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
const statusOptions = ["", "present", "absent", "late", "permission", "on_duty"];
const labels = { coach: "Coaches", camp_official: "Para Sports Officials", irp: "IRPs", ssa_official: "Samagra Shiksha Officials" };

const CampPeopleAttendance = ({ campId = null, canManageDirectories = false, showStaff = true }) => {
  const [date, setDate] = useState(todayIST);
  const [staff, setStaff] = useState([]);
  const [parents, setParents] = useState([]);
  const [tab, setTab] = useState(showStaff ? "staff" : "parents");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [personForm, setPersonForm] = useState({ person_type: "irp", name: "", district: "", mobile: "", designation: "" });
  const [responsibilities, setResponsibilities] = useState({ male_attendants: "", female_attendants: "", ssa_officials: "" });
  const [responsibilityCandidates, setResponsibilityCandidates] = useState([]);
  const [savingResponsibilities, setSavingResponsibilities] = useState(false);

  const fetchSheet = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.camp.getPeopleAttendanceSheet({ camp_id: campId || undefined, date });
      const data = response?.data || response;
      setStaff(data?.staff || []);
      setParents(data?.parents || []);
      if (!canManageDirectories && (data?.staff || []).length === 0 && (data?.parents || []).length > 0) {
        setTab("parents");
      }
      if (data?.responsibility_config) {
        setResponsibilities((current) => ({ ...current, ...(data.responsibility_config.assignments || {}) }));
        setResponsibilityCandidates(data.responsibility_config.candidates || []);
      }
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Unable to load camp attendance.", "error");
    } finally {
      setLoading(false);
    }
  }, [campId, canManageDirectories, date]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  const groupedStaff = useMemo(() => staff.reduce((groups, row) => {
    (groups[row.person_type] ||= []).push(row);
    return groups;
  }, {}), [staff]);

  const updateStaff = (type, id, field, value) => setStaff((rows) => rows.map((row) =>
    row.person_type === type && row.person_id === id ? { ...row, [field]: value } : row
  ));

  const updateParent = (id, field, value) => setParents((rows) => rows.map((row) => {
    if (row.athlete_id !== id) return row;
    const next = { ...row, [field]: value };
    if (field === "attendant_type" && value === "none") {
      next.attendant_count = 0;
      next.status = "absent";
      next.arrived_on = "";
      next.exited_on = "";
    } else if (field === "attendant_type" && value !== "none" && Number(row.attendant_count) === 0) {
      next.attendant_count = value === "both" ? 2 : 1;
      next.status = "present";
    }
    return next;
  }));

  const save = async () => {
    setSaving(true);
    try {
      await API.camp.savePeopleAttendance({ camp_id: campId || undefined, date, staff, parents });
      Swal.fire({ icon: "success", title: "Attendance Saved", timer: 1400, showConfirmButton: false });
      fetchSheet();
    } catch (error) {
      Swal.fire("Save Failed", error?.response?.data?.message || error?.message || "Unable to save attendance.", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveResponsibilities = async () => {
    setSavingResponsibilities(true);
    try {
      await API.camp.updatePeopleAttendanceResponsibilities({ camp_id: campId, assignments: responsibilities });
      Swal.fire({ icon: "success", title: "Attendance In-charges Updated", timer: 1400, showConfirmButton: false });
      fetchSheet();
    } catch (error) {
      Swal.fire("Save Failed", error?.response?.data?.message || "Unable to update attendance in-charges.", "error");
    } finally {
      setSavingResponsibilities(false);
    }
  };

  const createPerson = async (event) => {
    event.preventDefault();
    try {
      await API.camp.createAttendancePerson({ ...personForm, camp_id: campId });
      setPersonForm({ person_type: "irp", name: "", district: "", mobile: "", designation: "" });
      fetchSheet();
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Unable to add person.", "error");
    }
  };

  const removePerson = async (row) => {
    const result = await Swal.fire({ title: `Remove ${row.name}?`, icon: "warning", showCancelButton: true, confirmButtonText: "Remove" });
    if (!result.isConfirmed) return;
    await API.camp.deleteAttendancePerson(row.person_id);
    fetchSheet();
  };

  const personCell = (row) => <td className="p-2"><div className="font-semibold">{row.name}</div><div className="text-[10px] text-slate-500">{row.subtitle || row.mobile || ""}</div></td>;

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-bold text-slate-900"><FaCalendarCheck className="text-emerald-600" /> Camp People Attendance</h2>
          <p className="text-xs text-slate-500">Daily staff member counts and player-attendant stay history</p>
        </div>
        <div className="flex items-center gap-2">
          {showStaff && tab === "staff" && <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />}
          <button type="button" onClick={save} disabled={saving || loading} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"><FaSave /> {saving ? "Saving..." : "Save"}</button>
        </div>
      </div>

      {canManageDirectories && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div><h3 className="text-sm font-bold text-indigo-950">Attendance In-charge Assignment</h3><p className="text-[11px] text-indigo-700">Only the selected coach or manager sees and updates that group.</p></div>
            <button type="button" onClick={saveResponsibilities} disabled={savingResponsibilities} className="rounded-lg bg-indigo-700 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">{savingResponsibilities ? "Saving..." : "Save Assignments"}</button>
          </div>
          <div className="grid gap-2 md:grid-cols-3">
            {[["female_attendants", "Girls' Attendants"], ["male_attendants", "Boys' Attendants"], ["ssa_officials", "SSA Staff"]].map(([scope, label]) => (
              <label key={scope} className="text-[11px] font-bold text-slate-700">{label}
                <select value={responsibilities[scope] || ""} onChange={(event) => setResponsibilities((current) => ({ ...current, [scope]: event.target.value }))} className="mt-1 w-full rounded-lg border border-indigo-200 bg-white px-2 py-2 text-xs font-semibold">
                  <option value="">Not assigned</option>
                  {responsibilityCandidates.map((candidate) => <option key={`${scope}-${candidate.value}`} value={candidate.value}>{candidate.name} ({candidate.detail})</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
      )}

      {showStaff && <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button type="button" onClick={() => setTab("staff")} className={`rounded-lg px-3 py-2 text-xs font-bold ${tab === "staff" ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-600"}`}><FaUserShield className="mr-1 inline" /> Staff & Officials</button>
        <button type="button" onClick={() => setTab("parents")} className={`rounded-lg px-3 py-2 text-xs font-bold ${tab === "parents" ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-600"}`}><FaUsers className="mr-1 inline" /> Player Attendants</button>
      </div>}

      {loading ? <div className="py-8 text-center text-sm text-slate-500">Loading attendance...</div> : tab === "staff" && showStaff ? (
        <div className="space-y-5">
          {Object.entries(groupedStaff).map(([type, rows]) => <div key={type}>
            <h3 className="mb-2 text-xs font-extrabold uppercase tracking-wide text-slate-600">{labels[type] || type}</h3>
            {<div className="overflow-x-auto"><table className="min-w-full text-xs"><thead className="bg-slate-50 text-slate-600"><tr><th className="p-2 text-left">S.No</th><th className="p-2 text-left">Name</th>{["irp", "ssa_official"].includes(type) && <th className="p-2">Members</th>}<th className="p-2">Morning</th><th className="p-2">Evening</th><th className="p-2 text-left">Remarks</th>{canManageDirectories && type === "irp" && <th />}</tr></thead><tbody>
              {rows.map((row, rowIndex) => <tr key={`${type}-${row.person_id}`} className="border-t border-slate-100"><td className="p-2">{rowIndex + 1}</td>{personCell(row)}{["irp", "ssa_official"].includes(type) && <td className="p-2"><input aria-label="Members" type="number" min="1" max="255" value={row.member_count ?? 1} onChange={e => updateStaff(type,row.person_id,"member_count",e.target.value)} className="w-16 rounded border p-1" /></td>}<td className="p-2 text-center"><select value={row.morning_status || ""} onChange={(event) => updateStaff(type, row.person_id, "morning_status", event.target.value)} className="rounded border px-2 py-1">{statusOptions.map((status) => <option key={status} value={status}>{status ? status.replace("_", " ") : "Not marked"}</option>)}</select></td><td className="p-2 text-center"><select value={row.evening_status || ""} onChange={(event) => updateStaff(type, row.person_id, "evening_status", event.target.value)} className="rounded border px-2 py-1">{statusOptions.map((status) => <option key={status} value={status}>{status ? status.replace("_", " ") : "Not marked"}</option>)}</select></td><td className="p-2"><input value={row.remarks || ""} onChange={(event) => updateStaff(type, row.person_id, "remarks", event.target.value)} className="w-full rounded border px-2 py-1" /></td>{canManageDirectories && type === "irp" && <td><button type="button" onClick={() => removePerson(row)} className="text-rose-600"><FaTrash /></button></td>}</tr>)}
            </tbody></table></div>}
          </div>)}
          {Object.keys(groupedStaff).length === 0 && <p className="py-6 text-center text-sm text-slate-500">No staff attendance is assigned to this login.</p>}
        </div>
      ) : <AttendantStayTable rows={parents} onChange={updateParent} />}

      {canManageDirectories && <div className="border-t border-slate-200 pt-4"><form onSubmit={createPerson} className="space-y-2 rounded-xl border border-slate-200 p-3"><h3 className="text-sm font-bold">Add IRP / SSA Official</h3><div className="grid grid-cols-2 gap-2"><select value={personForm.person_type} onChange={(event) => setPersonForm({ ...personForm, person_type: event.target.value })} className="rounded border px-2 py-2 text-xs"><option value="irp">IRP</option><option value="ssa_official">SSA Official</option></select><input required placeholder="Name" value={personForm.name} onChange={(event) => setPersonForm({ ...personForm, name: event.target.value })} className="rounded border px-2 py-2 text-xs" /><input placeholder="District (IRP)" value={personForm.district} onChange={(event) => setPersonForm({ ...personForm, district: event.target.value })} className="rounded border px-2 py-2 text-xs" /><input placeholder="Designation (SSA)" value={personForm.designation} onChange={(event) => setPersonForm({ ...personForm, designation: event.target.value })} className="rounded border px-2 py-2 text-xs" /><input placeholder="Mobile" value={personForm.mobile} onChange={(event) => setPersonForm({ ...personForm, mobile: event.target.value })} className="rounded border px-2 py-2 text-xs" /></div><button className="inline-flex items-center gap-1 rounded bg-cyan-700 px-3 py-2 text-xs font-bold text-white"><FaPlus /> Add Person</button></form></div>}
    </section>
  );
};

export default CampPeopleAttendance;
